import { useLocation } from '@tanstack/react-router'
import { useState } from 'react'

import { TimelineView } from './view'
import { Modal } from '../../components/modal'
import { Button } from '../../components/button'
import { StepUpDialog } from '../../components/step-up'
import { ScreenError, ScreenLoading } from '../../components/state'
import { t } from '../../copy'
import { T } from '../../copy/text'
import {
  useAccountMembership,
  useApproveAction,
  useTimeline,
  useUndoAction,
  type TimelineFilter,
} from '../../app/queries'

/**
 * 屏 `timeline`「他们干过什么」。
 *
 * 这一层只做接线：取数、把筛选值往查询参数里送、把撤销与批准的结果收回来。
 * 长什么样在 `view.tsx`，一条长什么样在 `entry.tsx`。
 *
 * 筛选状态放在组件里而不是 URL 上：设计稿那三个 chip 是即时切换的视图开关，
 * 不是可以分享的地址。**但过滤本身在服务端做**——切一档就换一次请求，
 * 拿到的那一页已经是过滤过的了（流 A3 的规则 5）。
 *
 * ------------------------------------------------------------------
 * # 两个动作，两种门槛，顺序不能反
 *
 *	撤销这次改动   一下就走，**没有任何确认**。产品原则第 3 条要求这条路
 *	               比动手更好走，在它前面竖一块牌子是把原则反过来用
 *	我同意，去做吧  先一句说清代价的确认（点完就发生、退路在哪儿），
 *	               再一次「确认是你本人」，令牌绑到这条 actionId
 *
 * 「先确认要做什么」排在「确认是你本人」前面：反过来的话，
 * 他会先被要求输密码，然后才知道自己要确认的是什么。
 * ------------------------------------------------------------------
 */
export function TimelineScreen() {
  const [filter, setFilter] = useState<TimelineFilter>('all')
  const location = useLocation()
  const projectId = new URLSearchParams(location.searchStr).get('project_id') || undefined
  const timeline = useTimeline({ filter, projectId })
  const undo = useUndoAction()
  const approve = useApproveAction()
  const account = useAccountMembership()

  /** 正要批的那一条：先问「真要做吗」，确认完才弹二次验证。 */
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [stepUpId, setStepUpId] = useState<string | null>(null)

  if (timeline.isPending) return <ScreenLoading shape="timeline" width="reading" />
  if (timeline.error) return <ScreenError error={timeline.error} />

  // **判据是后端给的 capabilities，不是角色名。** 权限规则有两份的那天，
  // UI 上能点的和后端认的就对不上了（`GET /account` 那段契约说的就是这件事）。
  const canApprove = account.data?.capabilities.includes('approve_red') ?? false

  return (
    <>
      <TimelineView
        items={timeline.data?.items ?? []}
        filter={filter}
        onFilterChange={(next) => {
          // 换一档就把上一次的报错清掉，不然它会挂在一条已经看不见的记录上
          undo.reset()
          approve.reset()
          setFilter(next)
        }}
        onUndo={(actionId) => undo.mutate(actionId)}
        undoingId={undo.isPending ? undo.variables : undefined}
        // 撤不了时后端返回 409，`user_message` 就是 `undo.not_undoable_text` 那一句。
        // 只显示这一句：`code` 与 `developer_message` 永远不上屏
        undoError={
          undo.error && undo.variables
            ? { actionId: undo.variables, message: undo.error.message }
            : undefined
        }
        onApprove={(actionId) => {
          approve.reset()
          setPendingId(actionId)
        }}
        canApprove={canApprove}
        approvingId={approve.isPending ? approve.variables?.actionId : undefined}
        approveError={
          approve.error && approve.variables
            ? { actionId: approve.variables.actionId, message: approve.error.message }
            : undefined
        }
      />

      <Modal
        open={pendingId !== null}
        onOpenChange={(next) => {
          if (!next) setPendingId(null)
        }}
        title={t('timeline.approve.confirm.title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingId(null)}>
              <T id="account.stepup.cancel" />
            </Button>
            <Button
              variant="danger"
              data-testid="timeline-approve-confirm"
              onClick={() => {
                setStepUpId(pendingId)
                setPendingId(null)
              }}
            >
              <T id="timeline.action.approve" />
            </Button>
          </>
        }
      >
        <p className="m-0 text-npc-body-sm leading-[1.7] text-npc-ink-soft">
          <T id="timeline.approve.confirm.body" />
        </p>
      </Modal>

      <StepUpDialog
        open={stepUpId !== null}
        intent="approve_red_action"
        resource={stepUpId ?? ''}
        description={t('timeline.approve.confirm.body')}
        onCancel={() => setStepUpId(null)}
        onVerified={(token) => {
          const actionId = stepUpId
          setStepUpId(null)
          if (!actionId) return
          // 令牌是一次性的，走到这儿它已经烧掉了——所以**不重试**，
          // 也不悄悄再弹一次框。失败那一句由 approveError 挂在那条记录上。
          approve.mutate({ actionId, stepUpToken: token })
        }}
      />
    </>
  )
}
